// @file Manages whiteboard attachments

import { useReactiveSet } from '@@/bits/reactivity'
import { getEditorObject } from '@@/bits/tldraw/editor'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { Cid } from '@@/types'
import type { Link } from '@padlet/arvo'
import { type PadletAttachmentShapeProps } from '@padlet/whiteboard-schema'
import { defineStore } from 'pinia'
import { type TLShapeId } from 'tldraw'
import { computed } from 'vue'

export const WHITEBOARD_ATTACHMENTS_LOADED_ID = 'whiteboard-attachments-loaded'

export const useWhiteboardAttachmentsStore = defineStore('whiteboardAttachments', () => {
  const surfaceAttachmentsStore = useSurfaceAttachmentsStore()
  const surfacePostsStore = useSurfacePostsStore()

  const postCidsForLoadedAttachments = useReactiveSet<Cid>()
  const postCidsForErroredAttachments = useReactiveSet<Cid>()

  const addPostCidToLoadedAttachments = (attachmentLoadedPostCid: Cid): void => {
    postCidsForLoadedAttachments.value.add(attachmentLoadedPostCid)
  }

  const addPostCidToErroredAttachments = (attachmentErroredPostCid: Cid): void => {
    postCidsForErroredAttachments.value.add(attachmentErroredPostCid)
  }

  /**
   * Returns true if all attachments have been loaded or errored.
   */
  const hasLoadedOrErroredAllAttachments = computed(() => {
    const lengthOfAllLoadedOrErroredAttachments =
      postCidsForLoadedAttachments.value.size + postCidsForErroredAttachments.value.size
    return Object.keys(surfaceAttachmentsStore.linkDisplayAttributes).length === lengthOfAllLoadedOrErroredAttachments
  })

  const getLinkAttributesForShape = (shapeId: TLShapeId): Link | null => {
    const shape = getEditorObject()?.getShape(shapeId)
    if (shape == null) return null

    const post =
      shape.type === 'padlet-attachment-shape'
        ? surfacePostsStore.getPostByServerId((shape.props as PadletAttachmentShapeProps).postId.toString())
        : null
    if (post == null) return null

    const attachmentUrl = post.wish_content?.attachment_props?.url ?? post.attachment
    if (attachmentUrl == null) return null

    return surfaceAttachmentsStore.getLinkAttributesForUrl(attachmentUrl)
  }

  return {
    postCidsForLoadedAttachments,
    postCidsForErroredAttachments,
    addPostCidToLoadedAttachments,
    addPostCidToErroredAttachments,
    hasLoadedOrErroredAllAttachments,
    getLinkAttributesForShape,
  }
})
